import { useEffect, useState } from "react";
import { authedLogoBlob } from "../lib/api";

/**
 * Iter 79.6 — Authenticated logo renderer.
 *
 * Drop-in replacement for ``<img src={dataLogoSrc(filename)} />``.
 * Fetches the binary via an authenticated XHR (bearer token attached
 * by the api axios instance), exposes it as a session-scoped blob URL,
 * and revokes the blob URL on unmount or filename change to avoid
 * leaking memory.
 *
 * Renders ``fallback`` while loading or on auth/404 failure. Use the
 * shared placeholder pattern from the surrounding component when
 * possible so the empty-state UX matches.
 *
 * Props:
 *   filename   — DB-stored logo filename. ``null`` ⇒ render fallback.
 *   alt        — img alt text.
 *   className  — passed through to <img>.
 *   fallback   — React node rendered while loading / on failure.
 *   bustKey    — optional re-fetch trigger (e.g. company.last_updated_at)
 *                so a logo replacement is reflected without a page reload.
 *   onError    — optional callback when blob fetch fails (auth/404/network).
 *   "data-testid" — passed through.
 */
export default function AuthedLogo({
    filename,
    alt = "",
    className,
    fallback = null,
    bustKey,
    onError,
    ...rest
}) {
    const [src, setSrc] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!filename) {
            setSrc(null);
            return undefined;
        }
        let revoke = () => { };
        let cancelled = false;
        const controller = new AbortController();
        setFailed(false);
        (async () => {
            const blob = await authedLogoBlob(filename, { signal: controller.signal });
            if (cancelled) {
                if (blob) blob.revoke();
                return;
            }
            if (!blob) {
                setFailed(true);
                if (typeof onError === "function") onError();
                return;
            }
            revoke = blob.revoke;
            setSrc(blob.src);
        })();
        return () => {
            cancelled = true;
            controller.abort();
            revoke();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filename, bustKey]);

    if (!filename || failed || !src) return fallback;
    return <img src={src} alt={alt} className={className} {...rest} />;
}
